import { useEffect, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

import {
  AlertTriangle,
  Building2,
  Camera,
  CheckCircle,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Loader2,
  MapPin,
  PenTool,
} from "lucide-react";

interface ReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteName: string;
  completionPercentage: number;
}

export function ReportModal({
  open,
  onOpenChange,
  siteId,
  siteName,
  completionPercentage,
}: ReportModalProps) {
  const [auditData, setAuditData] = useState<any>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportFormat, setReportFormat] = useState<"pdf" | "word">("pdf");

  useEffect(() => {
    if (open) {
      // Load audit data from localStorage
      const projectData = JSON.parse(
        localStorage.getItem(`audit-${siteId}-project-data`) || "{}",
      );
      const siteData = JSON.parse(
        localStorage.getItem(`audit-${siteId}-site-data`) || "{}",
      );
      const completedWorks = JSON.parse(
        localStorage.getItem(`audit-${siteId}-completed-works`) || "{}",
      );
      const observations = JSON.parse(
        localStorage.getItem(`audit-${siteId}-observations`) || "{}",
      );
      const punchList = JSON.parse(
        localStorage.getItem(`audit-${siteId}-punch-list`) || "[]",
      );
      const photos = JSON.parse(
        localStorage.getItem(`audit-${siteId}-photos`) || "[]",
      );
      const signatures = JSON.parse(
        localStorage.getItem(`audit-${siteId}-signatures`) || "{}",
      );

      setAuditData({
        projectData,
        siteData,
        completedWorks,
        observations,
        punchList,
        photos,
        signatures,
      });
    }
  }, [open, siteId]);

  const generateReport = async (format: "pdf" | "word") => {
    setIsGenerating(true);
    setReportFormat(format);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const reportContent = createReportContent(auditData);

      if (format === "pdf") {
        // Generate PDF using browser's print functionality
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(reportContent);
          printWindow.document.close();
          printWindow.onload = () => {
            printWindow.print();
          };
        }
      } else {
        // Generate Word document
        const blob = new Blob([reportContent], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${siteName.replace(/\s+/g, "_")}_Audit_Report_${completionPercentage}%.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const createReportContent = (data: any) => {
    const currentDate = new Date().toLocaleDateString();
    const hasData =
      Object.keys(data.projectData || {}).length > 0 ||
      Object.keys(data.siteData || {}).length > 0 ||
      (data.punchList && data.punchList.length > 0);

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>EncoAudit Report - ${siteName}</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              line-height: 1.6; 
              color: #333;
            }
            .header { 
              text-align: center; 
              border-bottom: 3px solid #2563eb; 
              padding-bottom: 20px; 
              margin-bottom: 30px; 
            }
            .header h1 { color: #1e40af; margin: 0; }
            .completion-badge { 
              display: inline-block; 
              background: ${completionPercentage === 100 ? "#10b981" : "#f59e0b"}; 
              color: white; 
              padding: 4px 12px; 
              border-radius: 20px; 
              font-size: 14px; 
              margin-top: 10px;
            }
            .section { 
              margin-bottom: 30px; 
              page-break-inside: avoid; 
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              overflow: hidden;
            }
            .section-title { 
              background: #f8fafc; 
              padding: 15px; 
              font-weight: bold; 
              border-bottom: 1px solid #e5e7eb;
              color: #1e40af;
              font-size: 18px;
            }
            .section-content { padding: 20px; }
            .field-group { margin: 15px 0; display: flex; }
            .field-label { 
              font-weight: 600; 
              display: inline-block; 
              width: 180px; 
              color: #374151;
            }
            .field-value { 
              display: inline-block; 
              flex: 1;
              color: #6b7280;
            }
            .table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 15px 0; 
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            .table th, .table td { 
              border: 1px solid #d1d5db; 
              padding: 12px; 
              text-align: left; 
            }
            .table th { 
              background-color: #f3f4f6; 
              font-weight: 600;
              color: #374151;
            }
            .risk-high { color: #dc2626; font-weight: bold; }
            .risk-medium { color: #ea580c; font-weight: bold; }
            .risk-low { color: #16a34a; font-weight: bold; }
            .no-data { 
              text-align: center; 
              color: #9ca3af; 
              font-style: italic; 
              padding: 40px;
              background: #f9fafb;
              border-radius: 8px;
              margin: 20px 0;
            }
            .signature-section { margin-top: 50px; }
            .signature-box { 
              border: 2px solid #d1d5db; 
              height: 80px; 
              width: 300px; 
              margin: 15px 0; 
              border-radius: 4px;
              background: #fafafa;
            }
            .progress-bar {
              width: 100%;
              height: 20px;
              background: #e5e7eb;
              border-radius: 10px;
              overflow: hidden;
              margin: 10px 0;
            }
            .progress-fill {
              height: 100%;
              background: ${completionPercentage === 100 ? "#10b981" : "#3b82f6"};
              width: ${completionPercentage}%;
              transition: width 0.3s ease;
            }
            @media print { 
              body { margin: 0; } 
              .no-print { display: none; } 
              .section { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>EncoAudit Report</h1>
            <h2>${siteName}</h2>
            <div class="completion-badge">${completionPercentage}% Complete</div>
            <p>Generated on: ${currentDate}</p>
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
          </div>

          ${
            !hasData
              ? `
            <div class="no-data">
              <h3>Report Preview</h3>
              <p>This report contains ${completionPercentage}% of the audit data.</p>
              <p>Complete more sections to generate a comprehensive report.</p>
            </div>
          `
              : ""
          }

          ${
            data.projectData && Object.keys(data.projectData).length > 0
              ? `
            <div class="section">
              <div class="section-title">📋 Project Information</div>
              <div class="section-content">
                <div class="field-group">
                  <span class="field-label">Project Name:</span>
                  <span class="field-value">${data.projectData.projectName || "Not specified"}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Location:</span>
                  <span class="field-value">${data.projectData.town || "Not specified"}, ${data.projectData.postcode || ""}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Country:</span>
                  <span class="field-value">${data.projectData.country || "Not specified"}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Contractor:</span>
                  <span class="field-value">${data.projectData.contractor || "Not specified"}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Design Capacity (DC):</span>
                  <span class="field-value">${data.projectData.designCapacityDC || "Not specified"} kW</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Design Capacity (AC):</span>
                  <span class="field-value">${data.projectData.designCapacityAC || "Not specified"} kW</span>
                </div>
              </div>
            </div>
          `
              : `
            <div class="section">
              <div class="section-title">📋 Project Information</div>
              <div class="no-data">No project data entered yet</div>
            </div>
          `
          }

          ${
            data.siteData && Object.keys(data.siteData).length > 0
              ? `
            <div class="section">
              <div class="section-title">🏗️ Site Information</div>
              <div class="section-content">
                <div class="field-group">
                  <span class="field-label">Topography:</span>
                  <span class="field-value">${data.siteData.topography || "Not specified"}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Access:</span>
                  <span class="field-value">${data.siteData.access || "Not specified"}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Ground Conditions:</span>
                  <span class="field-value">${data.siteData.groundConditions || "Not specified"}</span>
                </div>
                <div class="field-group">
                  <span class="field-label">Safety:</span>
                  <span class="field-value">${data.siteData.safety || "Not specified"}</span>
                </div>
              </div>
            </div>
          `
              : `
            <div class="section">
              <div class="section-title">🏗️ Site Information</div>
              <div class="no-data">No site data entered yet</div>
            </div>
          `
          }

          ${
            data.punchList && data.punchList.length > 0
              ? `
            <div class="section">
              <div class="section-title">⚠️ Punch List Items (${data.punchList.length} items)</div>
              <div class="section-content">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${data.punchList
                      .map(
                        (item: any) => `
                      <tr>
                        <td>${item.title || "Untitled"}</td>
                        <td>${item.category || "General"}</td>
                        <td class="risk-${(item.priority || "low").toLowerCase()}">${item.priority || "Low"}</td>
                        <td>${item.status || "Open"}</td>
                        <td>${item.description || "No description"}</td>
                      </tr>
                    `,
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            </div>
          `
              : `
            <div class="section">
              <div class="section-title">⚠️ Punch List Items</div>
              <div class="no-data">No punch list items added yet</div>
            </div>
          `
          }

          <div class="signature-section">
            <div class="section">
              <div class="section-title">✍️ Signatures</div>
              <div class="section-content">
                <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 20px;">
                  <div>
                    <p><strong>Auditor:</strong></p>
                    <div class="signature-box"></div>
                    <p>Date: _______________</p>
                  </div>
                  <div>
                    <p><strong>Site Representative:</strong></p>
                    <div class="signature-box"></div>
                    <p>Date: _______________</p>
                  </div>
                  <div>
                    <p><strong>Project Manager:</strong></p>
                    <div class="signature-box"></div>
                    <p>Date: _______________</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style="margin-top: 40px; text-align: center; color: #6b7280; font-size: 12px;">
            <p>This report was generated automatically from the EncoAudit System</p>
            <p>Report completion: ${completionPercentage}% • Generated: ${currentDate}</p>
          </div>
        </body>
      </html>
    `;
  };

  const getSectionStatus = (sectionData: any) => {
    if (!sectionData || Object.keys(sectionData).length === 0) {
      return { status: "empty", color: "text-slate-400", icon: AlertTriangle };
    }
    const filledFields = Object.values(sectionData).filter(
      (value) => value !== "" && value !== null && value !== undefined,
    ).length;
    const totalFields = Object.keys(sectionData).length;

    if (filledFields === 0) {
      return { status: "empty", color: "text-slate-400", icon: AlertTriangle };
    } else if (filledFields === totalFields) {
      return { status: "complete", color: "text-green-600", icon: CheckCircle };
    } else {
      return { status: "partial", color: "text-blue-600", icon: ClipboardList };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Audit Report Preview
          </DialogTitle>
          <DialogDescription>
            Preview and generate reports with current audit data (
            {completionPercentage}% complete)
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="preview" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview">Report Preview</TabsTrigger>
            <TabsTrigger value="generate">Generate Report</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    {siteName}
                  </CardTitle>
                  <Badge
                    variant={
                      completionPercentage === 100 ? "default" : "secondary"
                    }
                  >
                    {completionPercentage}% Complete
                  </Badge>
                </div>
                <Progress value={completionPercentage} className="h-2" />
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: "Project Data",
                  data: auditData.projectData,
                  icon: FileText,
                },
                { title: "Site Data", data: auditData.siteData, icon: MapPin },
                {
                  title: "Completed Works",
                  data: auditData.completedWorks,
                  icon: CheckCircle,
                },
                {
                  title: "Observations",
                  data: auditData.observations,
                  icon: ClipboardList,
                },
                { title: "Photos", data: auditData.photos, icon: Camera },
                {
                  title: "Signatures",
                  data: auditData.signatures,
                  icon: PenTool,
                },
              ].map((section) => {
                const status = getSectionStatus(section.data);
                const Icon = status.icon;

                return (
                  <Card key={section.title}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <section.icon className="w-4 h-4 text-slate-500 dark:text-muted-foreground" />
                          <span className="font-medium">{section.title}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon className={`w-4 h-4 ${status.color}`} />
                          <span
                            className={`text-xs ${status.color} capitalize`}
                          >
                            {status.status}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {auditData.punchList && auditData.punchList.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    Punch List Items ({auditData.punchList.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {auditData.punchList
                      .slice(0, 3)
                      .map((item: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-slate-50 dark:bg-card rounded"
                        >
                          <span className="font-medium">
                            {item.title || "Untitled Item"}
                          </span>
                          <Badge
                            variant={
                              item.priority === "High"
                                ? "destructive"
                                : item.priority === "Medium"
                                  ? "default"
                                  : "secondary"
                            }
                          >
                            {item.priority || "Low"}
                          </Badge>
                        </div>
                      ))}
                    {auditData.punchList.length > 3 && (
                      <p className="text-sm text-slate-500 dark:text-muted-foreground">
                        +{auditData.punchList.length - 3} more items
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="generate" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Generate Report</CardTitle>
                <DialogDescription>
                  Generate a comprehensive report with all available audit data.
                  Reports can be generated at any completion level.
                </DialogDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Button
                    onClick={() => generateReport("pdf")}
                    disabled={isGenerating}
                    className="flex-1"
                  >
                    {isGenerating && reportFormat === "pdf" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Generate PDF
                  </Button>
                  <Button
                    onClick={() => generateReport("word")}
                    disabled={isGenerating}
                    variant="outline"
                    className="flex-1"
                  >
                    {isGenerating && reportFormat === "word" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Generate Word
                  </Button>
                </div>

                <Separator />

                <div className="text-sm text-slate-600 dark:text-muted-foreground">
                  <p className="font-medium mb-2">Report will include:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>
                      Project and site information (
                      {Object.keys(auditData.projectData || {}).length > 0
                        ? "✓"
                        : "○"}{" "}
                      Available)
                    </li>
                    <li>
                      Completed works summary (
                      {Object.keys(auditData.completedWorks || {}).length > 0
                        ? "✓"
                        : "○"}{" "}
                      Available)
                    </li>
                    <li>
                      Site observations (
                      {Object.keys(auditData.observations || {}).length > 0
                        ? "✓"
                        : "○"}{" "}
                      Available)
                    </li>
                    <li>
                      Punch list items ({auditData.punchList?.length || 0}{" "}
                      items)
                    </li>
                    <li>
                      Photo documentation ({auditData.photos?.length || 0}{" "}
                      photos)
                    </li>
                    <li>Signature sections (template included)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
